var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { computed, observable, reaction } from "mobx";
import { NOT_LOADED, ParameterizedRemoteData } from "../Infra/RemoteData";
import { NamedRoutes } from "./NamedRoutes";
import { SimpleDialogModel } from "./SimpleDialogModel";
import { FormModel } from "./FormModel";
export class AdminDataPolicyViewModel {
    constructor(container) {
        this.selectedDataSource = null;
        this.currentTab = "DATA_CLASSIFICATION";
        this.isRunningClassification = false;
        this.classificationRuns = [];
        this.selectedClassificationRun = null;
        this.datasetsForDataSource = [];
        this.tablesForDataset = [];
        this.selectedTableIds = [];
        this.selectedDataSetId = null;
        this.enforcementViews = [];
        this.reviewClassificationRunDialog = new SimpleDialogModel();
        this.overrideTagDialog = new SimpleDialogModel();
        this.deleteClassificationRunDialog = new SimpleDialogModel();
        this.dataSourceRegistrationDialog = new SimpleDialogModel();
        this.overrideTagFormModel = new FormModel({
            tagName: "",
            overrideReason: "",
        }, ["tagName"]);
        this.tagMetadata = [];
        this.pollingInterval = null;
        this.organizationsManager = container.organizationsManager;
        this.provisioningService = container.provisioningService;
        this.invalidationManager = container.invalidationManager;
        this.adminUIManager = container.adminUIManager;
        this.routingUIManager = container.routingUIManager;
        this.dataSourcesData = new ParameterizedRemoteData(this.invalidationManager, () => this.organizationId, () => this.loadDataSources());
        this.recentDataSources = new ParameterizedRemoteData(this.invalidationManager, () => this.organizationId, () => this.loadRecentDataSources());
        void this.loadTagMetadata();
        reaction(() => this.selectedDataSource, async (dataSource) => {
            this.stopPolling();
            if (dataSource === null || dataSource === void 0 ? void 0 : dataSource.id) {
                this.updateUrlWithDataSource(dataSource.id);
                await this.loadClassifications(dataSource.id);
                if (this.classificationRuns.some((run) => run.status === "IN_PROGRESS")) {
                    this.isRunningClassification = true;
                    this.startPolling();
                }
                if (this.currentTab === "DATA_ENFORCEMENT") {
                    await this.loadEnforcementViews();
                }
            }
        }, { fireImmediately: true });
        reaction(() => this.currentTab, async (tab) => {
            var _a;
            if ((_a = this.selectedDataSource) === null || _a === void 0 ? void 0 : _a.id) {
                this.updateUrlWithDataSource(this.selectedDataSource.id);
            }
            if (tab === "DATA_ENFORCEMENT") {
                await this.loadEnforcementViews();
            }
        }, { fireImmediately: true });
        this.initializeFromUrl();
    }
    stop() {
        this.stopPolling();
    }
    updateUrlWithDataSource(id) {
        this.routingUIManager.navigateTo(this.currentTab === "DATA_ENFORCEMENT"
            ? NamedRoutes.ADMIN_DATA_POLICY_DATA_ENFORCEMENT
            : NamedRoutes.ADMIN_DATA_POLICY_DATA_CLASSIFICATION, { dataSourceId: id });
    }
    initializeFromUrl() {
        var _a;
        const currentRoute = ((_a = this.routingUIManager.currentRoute) === null || _a === void 0 ? void 0 : _a.routeName) || "";
        if (currentRoute.includes("ENFORCEMENT")) {
            this.setCurrentTab("DATA_ENFORCEMENT");
        }
        else {
            this.setCurrentTab("DATA_CLASSIFICATION");
        }
    }
    handleTabChange(tab) {
        this.setCurrentTab(tab);
    }
    async openReviewClassificationRunDialog(classificationRunId) {
        var _a;
        this.selectedClassificationRun = await this.getClassificationRun(classificationRunId);
        this.reviewClassificationRunDialog.open = true;
        if ((_a = this.selectedDataSource) === null || _a === void 0 ? void 0 : _a.id) {
            await this.updateDataSourceAsViewed(this.selectedDataSource.id);
            this.recentDataSources.reload();
        }
    }
    closeReviewClassificationRunDialog() {
        this.reviewClassificationRunDialog.open = false;
        this.selectedClassificationRun = null;
        void this.loadClassifications(this.selectedDataSource.id);
    }
    get organizationId() {
        return this.adminUIManager.organizationId;
    }
    get userId() {
        return this.adminUIManager.userId;
    }
    get showSpinner() {
        if (this.recentDataSources.value === NOT_LOADED)
            return true;
        return false;
    }
    get showRecentDataSourcesTable() {
        if (this.recentDataSources.value === NOT_LOADED)
            return false;
        return this.recentDataSources.value.length > 0;
    }
    canPromote(classificationData) {
        return classificationData.classificationEntities.every((entity) => entity.columns.every((column) => column.columnTags.length === 0 || column.columnTags.every((tag) => tag.verified)));
    }
    navigateToDataSettings() {
        this.routingUIManager.navigateTo(NamedRoutes.ADMIN_DATA_SETTINGS);
    }
    navigateToStudy(projectId) {
        this.routingUIManager.navigateTo(NamedRoutes.PROJECTS_PROJECT_MANAGER, { projectId });
    }
    setSelectedDataSource(dataSource) {
        this.selectedDataSource = dataSource;
    }
    get dataSources() {
        return this.dataSourcesData.value;
    }
    async loadDataSources() {
        if (!this.organizationId)
            return [];
        return await this.provisioningService.getDataSources(this.organizationId);
    }
    async loadRecentDataSources() {
        if (!this.organizationId)
            return [];
        return await this.provisioningService.getRecentlyViewedDataSources(this.organizationId);
    }
    async loadClassifications(dataSourceId) {
        if (!this.organizationId)
            return [];
        const allRuns = await this.provisioningService.getClassifications(this.organizationId, dataSourceId);
        const enabledRuns = allRuns.filter((run) => run.enabled);
        const sortedEnabledRuns = enabledRuns.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        const mostRecentEnabledRun = sortedEnabledRuns.length > 0 ? [sortedEnabledRuns[0]] : [];
        const disabledRuns = allRuns.filter((run) => !run.enabled);
        const sortedDisabledRuns = disabledRuns.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        const mostRecentDisabledRun = sortedDisabledRuns.length > 0 ? [sortedDisabledRuns[0]] : [];
        this.classificationRuns = [...mostRecentEnabledRun, ...mostRecentDisabledRun];
    }
    async updateDataSourceAsViewed(dataSourceId) {
        if (!this.organizationId)
            return;
        await this.provisioningService.updateDataSourceAsViewed(this.organizationId, dataSourceId);
    }
    async runClassification() {
        if (!this.organizationId)
            return;
        if (!this.selectedDataSource)
            return;
        await this.provisioningService.runClassification(this.organizationId, this.selectedDataSource.id);
        await this.loadClassifications(this.selectedDataSource.id);
        this.startPolling();
    }
    async getClassificationRun(classificationRunId) {
        if (!this.organizationId)
            return null;
        const run = await this.provisioningService.getClassificationRun(this.organizationId, classificationRunId);
        if (run === null || run === void 0 ? void 0 : run.classificationEntities) {
            run.classificationEntities.sort((a, b) => a.name.localeCompare(b.name));
        }
        return run;
    }
    setCurrentTab(tab) {
        this.currentTab = tab;
    }
    get tabsToShow() {
        return [
            { value: "DATA_CLASSIFICATION", label: "Data Classifications" },
            { value: "DATA_ENFORCEMENT", label: "Data Enforcement Views" },
        ];
    }
    async updateColumnClassification(columnId, input) {
        const entity = this.selectedClassificationRun.classificationEntities.find((e) => e.columns.some((c) => c.id === columnId));
        if (!entity || !this.organizationId || !this.userId)
            return;
        await this.provisioningService.updateColumnClassification(this.organizationId, this.selectedClassificationRun.id, entity.id, columnId, input);
        const updatedRun = await this.getClassificationRun(this.selectedClassificationRun.id);
        this.selectedClassificationRun = updatedRun;
    }
    verifyColumnTag(columnId, tagId, overrideReason) {
        var _a;
        return this.updateColumnClassification(columnId, {
            verified: true,
            verifiedBy: (_a = this.userId) !== null && _a !== void 0 ? _a : undefined,
            overrideTagId: tagId,
            overrideReason: overrideReason !== null && overrideReason !== void 0 ? overrideReason : undefined,
        });
    }
    async markAsPatientIdentifier(column, entityId) {
        var _a, _b;
        if (!this.organizationId)
            return;
        if (!((_a = this.selectedDataSource) === null || _a === void 0 ? void 0 : _a.id))
            return;
        if (!((_b = this.selectedClassificationRun) === null || _b === void 0 ? void 0 : _b.id))
            return;
        const entity = this.selectedClassificationRun.classificationEntities.find((e) => e.id === entityId);
        const existingMetadata = (entity === null || entity === void 0 ? void 0 : entity.metadata) ? JSON.parse(entity.metadata) : {};
        existingMetadata.patientIdColumnName = column.name;
        await this.provisioningService.updateDataWarehouseClassificationEntity(this.organizationId, this.selectedClassificationRun.id, entityId, { metadata: JSON.stringify(existingMetadata) });
        if (entity) {
            entity.metadata = JSON.stringify(existingMetadata);
        }
    }
    async openOverrideTagDialog(columnId, tagId) {
        this.overrideTagFormModel.updateInput({
            tagName: tagId,
            overrideReason: "",
        });
        const confirmed = await this.overrideTagDialog.showAndWait();
        if (!confirmed)
            return;
        await this.verifyColumnTag(columnId, this.overrideTagFormModel.input.tagName, this.overrideTagFormModel.input.overrideReason);
        this.closeOverrideTagDialog();
    }
    closeOverrideTagDialog() {
        this.overrideTagDialog.open = false;
    }
    async onDeleteClassificationRun(classificationRunId) {
        if (!this.organizationId)
            return;
        const confirmed = await this.deleteClassificationRunDialog.showAndWait();
        if (!confirmed)
            return;
        await this.provisioningService.deleteClassificationRun(this.organizationId, classificationRunId);
        await this.loadClassifications(this.selectedDataSource.id);
    }
    async promoteClassification(classificationRun) {
        if (!this.organizationId)
            return;
        await this.provisioningService.updateClassificationRun(this.organizationId, classificationRun.id, {
            enabled: true,
            dataSourceId: this.selectedDataSource.id,
            status: classificationRun.status,
        });
        await this.loadClassifications(this.selectedDataSource.id);
    }
    async loadTagMetadata() {
        if (!this.organizationId)
            return;
        this.tagMetadata = await this.provisioningService.getTagMetadata(this.organizationId);
    }
    async openDataSourceRegistrationDialog() {
        this.dataSourceRegistrationDialog.open = true;
    }
    async closeDataSourceRegistrationDialog() {
        this.dataSourceRegistrationDialog.open = false;
        await this.loadClassifications(this.selectedDataSource.id);
    }
    startPolling() {
        if (this.pollingInterval !== null)
            return;
        this.pollingInterval = window.setInterval(async () => {
            var _a;
            if (!((_a = this.selectedDataSource) === null || _a === void 0 ? void 0 : _a.id)) {
                this.stopPolling();
                return;
            }
            await this.loadClassifications(this.selectedDataSource.id);
            this.isRunningClassification = this.classificationRuns.some((run) => run.status === "IN_PROGRESS");
            if (!this.isRunningClassification) {
                this.stopPolling();
            }
        }, 2000);
    }
    stopPolling() {
        if (this.pollingInterval !== null) {
            window.clearInterval(this.pollingInterval);
            this.pollingInterval = null;
        }
    }
    async loadEnforcementViews() {
        var _a;
        if (!this.organizationId || !((_a = this.selectedDataSource) === null || _a === void 0 ? void 0 : _a.id))
            return;
        this.enforcementViews = await this.provisioningService.getEnforcementViews(this.organizationId, this.selectedDataSource.id);
    }
    get allTablesReviewed() {
        var _a, _b;
        return ((_b = (_a = this.selectedClassificationRun) === null || _a === void 0 ? void 0 : _a.classificationEntities.every((entity) => entity.dataWarehouseClassificationEntityMetrics.classifiedColumns ===
            entity.dataWarehouseClassificationEntityMetrics.verifiedColumns)) !== null && _b !== void 0 ? _b : false);
    }
}
__decorate([
    observable
], AdminDataPolicyViewModel.prototype, "dataSourcesData", void 0);
__decorate([
    observable
], AdminDataPolicyViewModel.prototype, "selectedDataSource", void 0);
__decorate([
    observable
], AdminDataPolicyViewModel.prototype, "recentDataSources", void 0);
__decorate([
    observable
], AdminDataPolicyViewModel.prototype, "currentTab", void 0);
__decorate([
    observable
], AdminDataPolicyViewModel.prototype, "isRunningClassification", void 0);
__decorate([
    observable
], AdminDataPolicyViewModel.prototype, "classificationRuns", void 0);
__decorate([
    observable
], AdminDataPolicyViewModel.prototype, "selectedClassificationRun", void 0);
__decorate([
    observable
], AdminDataPolicyViewModel.prototype, "datasetsForDataSource", void 0);
__decorate([
    observable
], AdminDataPolicyViewModel.prototype, "tablesForDataset", void 0);
__decorate([
    observable
], AdminDataPolicyViewModel.prototype, "selectedTableIds", void 0);
__decorate([
    observable
], AdminDataPolicyViewModel.prototype, "selectedDataSetId", void 0);
__decorate([
    observable
], AdminDataPolicyViewModel.prototype, "enforcementViews", void 0);
__decorate([
    observable
], AdminDataPolicyViewModel.prototype, "tagMetadata", void 0);
__decorate([
    observable
], AdminDataPolicyViewModel.prototype, "pollingInterval", void 0);
__decorate([
    computed
], AdminDataPolicyViewModel.prototype, "organizationId", null);
__decorate([
    computed
], AdminDataPolicyViewModel.prototype, "userId", null);
__decorate([
    computed
], AdminDataPolicyViewModel.prototype, "showSpinner", null);
__decorate([
    computed
], AdminDataPolicyViewModel.prototype, "showRecentDataSourcesTable", null);
__decorate([
    computed
], AdminDataPolicyViewModel.prototype, "dataSources", null);
__decorate([
    computed
], AdminDataPolicyViewModel.prototype, "tabsToShow", null);
__decorate([
    computed
], AdminDataPolicyViewModel.prototype, "allTablesReviewed", null);
